import { createContext, useContext } from 'react';
import type { ObjectView, TableModel } from '../model/table';
import type { ChoiceStep } from '../protocol/ui';
import type { Resolved } from './bindings.ts';
import type { ControlName, Presentation, PresentationV2, Shape, Template } from './types.ts';
import { hasGlyph } from './Glyph';
import { templateFace } from '../faces/builtin.ts';
import { flatten, lodForWidth, matchFace, resolveFace, slotValues, type FaceContext, type FlatFace, type ResolvedFace } from '../faces/resolve.ts';
import type { Lod } from '../faces/types.ts';
import { createArtResolver, frameIndex, type ArtResolver } from '../faces/art.ts';
import { artAsset } from '../art/assets.ts';
import { adjacencyDescriber } from '../model/adjacency.ts';

export interface ResolvedTemplate { shape: Shape; slots: Record<string, Resolved | null> }

/** In-place picking: objects that answer the current draft step when activated. */
export interface PickTargets { values: Map<string, string | number | boolean>; answer: (value: string | number | boolean) => void }

export interface PresentationApi {
  presentation: Presentation | PresentationV2 | null;
  render: (object: ObjectView, zoneKind: string | null) => ResolvedTemplate | null;
  /** Resolves the object's face (package face, else its v1 template as a built-in face) at a detail level. */
  face: (object: ObjectView, zoneKind: string | null, lod: Lod) => ResolvedFace | null;
  icon: (key: string) => Resolved | null;
  control: (step: ChoiceStep, group: string) => ControlName | null;
  groupOrder: (group: string) => { order: number; hotkey: string | null };
  quickSubmit: boolean;
  /** Extra line for a move or option that names a board node or edge (interactions.pickLists.describe); null when the layout does not ask. */
  describeSubject: ((objectId: string) => string | null) | null;
  pickTargets: PickTargets | null;
}

const NO_PRESENTATION: PresentationApi = {
  presentation: null, render: () => null, face: () => null, icon: key => (hasGlyph(key) ? { kind: 'glyph', name: key } : null),
  control: () => null, groupOrder: () => ({ order: 0, hotkey: null }), quickSubmit: false, describeSubject: null, pickTargets: null,
};

export const PresentationContext = createContext<PresentationApi>(NO_PRESENTATION);
export const usePresentation = () => useContext(PresentationContext);
export { lodForWidth };

/** Faces a package defines, flattened once; a face that fails to flatten is skipped. */
function packageFaces(presentation: PresentationV2): FlatFace[] {
  const defs = presentation.faces?.faces ?? [];
  const byId = new Map(defs.map(f => [f.id, f]));
  const out: FlatFace[] = [];
  for (const f of defs) {
    if (!f.match) continue;
    try { out.push({ ...flatten(f.id, byId), match: f.match }); } catch { /* the validator reports it */ }
  }
  return out;
}

/** Flattened package faces and resolved faces, kept per presentation across table updates. */
const faceStore = new WeakMap<object, { faces: FlatFace[]; cache: Map<string, ResolvedFace | null> }>();
function sharedFaces(presentation: Presentation | PresentationV2) {
  let s = faceStore.get(presentation);
  if (!s) { s = { faces: 'format' in presentation ? packageFaces(presentation) : [], cache: new Map() }; faceStore.set(presentation, s); }
  return s;
}

function legacyFace(t: Template): FlatFace {
  const f = templateFace(t);
  return { id: f.id, size: { radius: 0, ...f.size! }, match: t.match, layers: f.layers!, back: null, lod: undefined, legacy: true, shape: t.shape };
}

export function createPresentationApi(presentation: Presentation | PresentationV2 | null, table: TableModel, seatColor: (owner: string) => string | null, pickTargets: PickTargets | null): PresentationApi {
  if (!presentation) return { ...NO_PRESENTATION, pickTargets };
  const { theme, templates, interactions } = presentation;

  const iconFrames = 'format' in presentation ? frameIndex(presentation.atlases ?? null) : null;
  const icon = (key: string): Resolved | null => {
    const target = theme.icons?.[key];
    if (target) {
      const frame = iconFrames?.get(target);
      // Atlas coordinates use the complete native sheet, not a selected smaller
      // derivative's dimensions. Native delivery also avoids fractional gutters.
      const sheet = frame ? artAsset(presentation, frame.asset, 4096, 1) : null;
      if (frame && sheet) return { kind: 'image', url: sheet.url, frame: frame.frame, sheet: { w: sheet.width, h: sheet.height } };
      const url = artAsset(presentation, target, 32)?.url;
      if (url) return { kind: 'image', url };
      if (hasGlyph(target)) return { kind: 'glyph', name: target };
    }
    return hasGlyph(key) ? { kind: 'glyph', name: key } : null;
  };

  // v1 templates become built-in faces; package faces (presentation-2) take precedence.
  const legacy = templates.templates.map(legacyFace);
  const shared = sharedFaces(presentation);
  const faces = shared.faces;
  // Resolved faces outlive one table: an unchanged object keeps the same face, so memoized faces skip re-rendering.
  const cache = shared.cache;
  if (cache.size > 4000) cache.clear();
  const artByLod = new Map<Lod, ArtResolver>();
  const widths = { chip: 40, thumb: 90, full: 240, zoom: 640 };
  const artAt = (lod: Lod) => {
    if (!('format' in presentation) || !presentation.art) return null;
    if (!artByLod.has(lod)) artByLod.set(lod, createArtResolver(presentation.atlases ?? null, presentation.art,
      id => artAsset(presentation, id, 4096, 1), id => artAsset(presentation, id, widths[lod])));
    return artByLod.get(lod)!;
  };

  const context = (object: ObjectView, lod: Lod = 'full'): FaceContext => ({
    object: { label: object.label, owner: object.owner, count: object.count, kind: object.kind, catalog: object.catalogKey, fields: object.fields },
    catalog: object.catalog, resources: table.resources,
    assetUrl: (id: string) => artAsset(presentation, id, widths[lod])?.url ?? null,
    icon, map: (name: string, value: string) => theme.maps?.[name]?.[value] ?? null, seatColor,
    token: name => `var(--${name === 'surface' ? 'surface-0' : name === 'surface-raised' ? 'surface-1' : name})`,
    ...(artAt(lod) ? { art: (slot: string) => artAt(lod)!({ kind: object.kind, catalogKey: object.catalogKey, tags: object.tags, faceDown: object.faceDown }, slot) } : {}),
  });

  const face = (object: ObjectView, zoneKind: string | null, lod: Lod): ResolvedFace | null => {
    const key = `${object.id}|${object.kind}|${object.catalogKey}|${object.label}|${object.tags.join(",")}|${zoneKind}|${lod}|${object.faceDown}|${object.owner}|${object.owner ? seatColor(object.owner) : ""}|${object.count}|${JSON.stringify(object.fields)}`;
    if (cache.has(key)) return cache.get(key)!;
    const target = { kind: object.kind, catalogKey: object.catalogKey, tags: object.tags, faceDown: object.faceDown };
    const flat = matchFace(faces, target, zoneKind) ?? matchFace(legacy, target, zoneKind);
    const resolved = flat ? resolveFace(flat, context(object, lod), flat.legacy ? 'full' : lod, object.faceDown && !flat.legacy ? 'back' : 'front') : null;
    cache.set(key, resolved);
    return resolved;
  };

  // presentation-1 slot rendering, through the template's built-in face.
  const render = (object: ObjectView, zoneKind: string | null): ResolvedTemplate | null => {
    const flat = matchFace(legacy, { kind: object.kind, catalogKey: object.catalogKey, tags: object.tags, faceDown: object.faceDown }, zoneKind);
    if (!flat) return null;
    return { shape: flat.shape!, slots: slotValues(resolveFace(flat, context(object), 'full')) };
  };

  const control = (step: ChoiceStep, group: string): ControlName | null => {
    for (const rule of interactions.controls ?? []) {
      const m = rule.match;
      if (m.type && m.type !== step.type) continue;
      if (m.field && m.field !== step.field) continue;
      if (m.group && m.group !== group) continue;
      if (m.keyPrefix !== undefined && !(step.type === 'allocate' && step.keys.every(k => k.key.startsWith(m.keyPrefix!)))) continue;
      if (m.hasObjects !== undefined && (step.type !== 'pick' || m.hasObjects !== step.options.every(o => !!o.objectId))) continue;
      return validControl(rule.control, step) ? rule.control : null;
    }
    return null;
  };

  const groups = new Map((interactions.groups ?? []).map((g, i) => [g.group, { order: g.order ?? i, hotkey: g.hotkey ?? null }]));
  return {
    presentation, render, face, icon, control, quickSubmit: interactions.quickSubmit ?? false, pickTargets,
    describeSubject: typeof interactions.pickLists === 'object' && interactions.pickLists?.describe === 'adjacent-regions' ? adjacencyDescriber(table) : null,
    groupOrder: group => groups.get(group) ?? { order: 1000, hotkey: null },
  };
}

/** An invalid pairing falls back to the client default (presentation contract section 5.1). */
function validControl(control: ControlName, step: ChoiceStep): boolean {
  switch (control) {
    case 'textarea': return step.type === 'text';
    case 'list': return step.type === 'pick';
    case 'buttons': return step.type === 'pick' && (step.max ?? 1) === 1 && step.options.length <= 12;
    case 'in-place': return step.type === 'pick' && step.options.length > 0 && step.options.every(o => !!o.objectId);
    case 'ordered': return step.type === 'pick' && !!step.ordered;
    case 'payment': return step.type === 'allocate';
    case 'stepper': return step.type === 'allocate' || step.type === 'number';
  }
}
